import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableLazyLoad,
} from '@web/shared/ui';
import { AdminDictionaryListAction } from '../../enums/admin-dictionary-list-action';
import { AdminDictionaryListItemAction } from '../../enums/admin-dictionary-list-item-action';
import { DictionaryCollectionFilter } from '../../../shared/models/dictionary-collection-filter';
import { DictionaryCollectionItem } from '../../../shared/models/dictionary-collection-item';
import { Route, RouteParam } from '@web/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { AdminDictionaryProductAttributeDataSourceService } from '../../services/admin-dictionary-product-attribute-data-source.service';

@Component({
  selector: 'app-admin-dictionary-product-attribute-list',
  imports: [TableComponent],
  templateUrl: './admin-dictionary-product-attribute-list.component.html',
  styleUrl: './admin-dictionary-product-attribute-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDictionaryProductAttributeListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<DictionaryCollectionItem, DictionaryCollectionFilter, AdminDictionaryListItemAction, never, AdminDictionaryListAction>).create();
  /* prettier-ignore */
  private readonly dictionaryDataSource = inject(AdminDictionaryProductAttributeDataSourceService);

  public readonly createAttributeVisibility = input<boolean>(false);
  public readonly removeSuccessEvent = output<void>();

  protected tableConfig = this.buildTableConfig();

  private readonly destroyRef = inject(DestroyRef);

  private buildTableConfig(): TableConfig<
    DictionaryCollectionItem,
    DictionaryCollectionFilter,
    AdminDictionaryListItemAction,
    never,
    AdminDictionaryListAction
  > {
    return this.tableBuilder
      .columns((builder) => [
        builder
          .initText('label')
          .filterKey('label')
          .filterable(true)
          .width('25rem')
          .label('Etykieta')
          .build(),

        builder
          .initDate('createdAt')
          .filterKey('label')
          .filterable(true)
          .label('Data dodania')
          .build(),
      ])
      .filters((builder) => [
        builder.initText('label').build(),
        builder.initDateRange('createdAt').build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminDictionaryListItemAction.Edit)
          .visibility((model) => model.canBeEdited)
          .routerLink(
            (provider) => (model) =>
              provider.routerLink(Route.DictionaryProductAttributeEdit, {
                [RouteParam.Guid]: model.id,
              }),
          )
          .tooltip('Edycja')
          .icon(Icon.Pencil)
          .build(),

        builder
          .initCommand(AdminDictionaryListItemAction.Remove)
          .visibility((model) => model.canBeRemoved)
          .command((item) => this.confirmAndRemove(item))
          .tooltip('Usuń')
          .icon(Icon.Trash)
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(AdminDictionaryListAction.Create)
          .routerLink((provider) =>
            provider.routerLink(Route.DictionaryProductAttributeCreate),
          )
          .visibility(() => of(this.createAttributeVisibility()))
          .label('Dodaj atrybut')
          .build(),
      ])
      .dataKey('id')
      .dataSource({
        fetch: (lazyLoad: TableLazyLoad<DictionaryCollectionFilter>) =>
          this.dictionaryDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private confirmAndRemove(item: DictionaryCollectionItem): void {
    this.dictionaryDataSource
      .confirmAndRemoveById(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.removeSuccessEvent.emit());
  }
}
